import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const ExportModal = ({ isOpen, onClose, onExport, selectedCount }) => {
  const [exportConfig, setExportConfig] = useState({
    format: 'csv',
    dateRange: 'filtered',
    includeSystemInfo: true,
    includeChangeDetails: true,
    includeRelatedEntries: false,
    customDateFrom: '',
    customDateTo: '',
    selectedFields: [
      'timestamp',
      'user',
      'action',
      'invoiceReference',
      'description',
      'fieldsChanged'
    ]
  });

  if (!isOpen) return null;

  const formatOptions = [
    { value: 'csv', label: 'CSV (Comma Separated)' },
    { value: 'xlsx', label: 'Excel (.xlsx)' },
    { value: 'pdf', label: 'PDF Report' }
  ];

  const dateRangeOptions = [
    { value: 'filtered', label: 'Current Filter Results' },
    { value: 'all', label: 'All Audit Entries' },
    { value: 'custom', label: 'Custom Date Range' }
  ];

  const availableFields = [
    { value: 'timestamp', label: 'Timestamp' },
    { value: 'user', label: 'User' },
    { value: 'action', label: 'Action Type' },
    { value: 'invoiceReference', label: 'Invoice Reference' },
    { value: 'description', label: 'Description' },
    { value: 'fieldsChanged', label: 'Fields Changed' },
    { value: 'sessionId', label: 'Session ID' },
    { value: 'ipAddress', label: 'IP Address' },
    { value: 'userAgent', label: 'User Agent' }
  ];

  const handleConfigChange = (key, value) => {
    setExportConfig(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleFieldToggle = (fieldValue, checked) => {
    setExportConfig(prev => ({
      ...prev,
      selectedFields: checked 
        ? [...prev?.selectedFields, fieldValue]
        : prev?.selectedFields?.filter(f => f !== fieldValue)
    }));
  };

  const handleExport = () => {
    onExport(exportConfig);
    onClose();
  };

  const getEstimatedSize = () => {
    const baseSize = selectedCount || 1000;
    const fieldMultiplier = exportConfig?.selectedFields?.length * 0.1;
    const formatMultiplier = exportConfig?.format === 'pdf' ? 2 : 1;
    
    return Math.round(baseSize * fieldMultiplier * formatMultiplier);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-background/80 backdrop-blur-sm"
        onClick={onClose}
      />
      {/* Modal */}
      <div className="relative bg-card border border-border rounded-lg shadow-moderate max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Icon name="Download" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">
                Export Audit Trail
              </h2>
              <p className="text-sm text-muted-foreground">
                Configure your audit trail export settings
              </p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            iconName="X"
          />
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          <div className="space-y-6">
            {/* Export Format */}
            <div>
              <Select
                label="Export Format"
                description="Choose the format for your export file"
                options={formatOptions}
                value={exportConfig?.format}
                onChange={(value) => handleConfigChange('format', value)}
              />
            </div>

            {/* Date Range */}
            <div>
              <Select
                label="Date Range"
                description="Select which entries to include in the export"
                options={dateRangeOptions}
                value={exportConfig?.dateRange}
                onChange={(value) => handleConfigChange('dateRange', value)}
              />
              
              {exportConfig?.dateRange === 'custom' && (
                <div className="mt-3 grid grid-cols-2 gap-3">
                  <Input
                    type="date"
                    label="From Date"
                    value={exportConfig?.customDateFrom}
                    onChange={(e) => handleConfigChange('customDateFrom', e?.target?.value)}
                  />
                  <Input
                    type="date"
                    label="To Date"
                    value={exportConfig?.customDateTo}
                    onChange={(e) => handleConfigChange('customDateTo', e?.target?.value)}
                  />
                </div>
              )}
            </div>

            {/* Export Options */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Export Options</h3>
              <div className="space-y-3">
                <Checkbox
                  label="Include System Information"
                  description="Session ID, IP address, user agent details"
                  checked={exportConfig?.includeSystemInfo}
                  onChange={(e) => handleConfigChange('includeSystemInfo', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Include Change Details"
                  description="Before/after values for field changes"
                  checked={exportConfig?.includeChangeDetails}
                  onChange={(e) => handleConfigChange('includeChangeDetails', e?.target?.checked)}
                />
                
                <Checkbox
                  label="Include Related Entries"
                  description="Link related audit entries together"
                  checked={exportConfig?.includeRelatedEntries}
                  onChange={(e) => handleConfigChange('includeRelatedEntries', e?.target?.checked)}
                />
              </div>
            </div>

            {/* Field Selection */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3">Fields to Include</h3>
              <div className="grid grid-cols-2 gap-2">
                {availableFields?.map((field) => (
                  <Checkbox
                    key={field?.value}
                    label={field?.label}
                    checked={exportConfig?.selectedFields?.includes(field?.value)}
                    onChange={(e) => handleFieldToggle(field?.value, e?.target?.checked)}
                  />
                ))}
              </div>
            </div>

            {/* Export Summary */}
            <div className="bg-muted/30 rounded-lg p-4">
              <h3 className="text-sm font-medium text-foreground mb-3">Export Summary</h3>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Format:</span>
                  <span className="text-foreground font-medium">
                    {formatOptions?.find(f => f?.value === exportConfig?.format)?.label}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entries:</span>
                  <span className="text-foreground font-medium">
                    {selectedCount ? `${selectedCount} selected` : 'All filtered results'}
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Fields:</span>
                  <span className="text-foreground font-medium">
                    {exportConfig?.selectedFields?.length} selected
                  </span>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Estimated Size:</span>
                  <span className="text-foreground font-medium">
                    ~{getEstimatedSize()} KB
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border">
          <div className="text-sm text-muted-foreground">
            {exportConfig?.selectedFields?.length === 0 && (
              <span className="text-warning">Please select at least one field to export</span>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            
            <Button
              variant="default"
              onClick={handleExport}
              disabled={exportConfig?.selectedFields?.length === 0}
              iconName="Download"
              iconPosition="left"
            >
              Export Audit Trail
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportModal;